import React from "react";
import { fetchConsolidationGeotabComparison } from "../api/geotabApi.js";

// Comparaison Geotab d'une journée : une seule lecture par journée et période, partagée par la liste, les relevés et la preuve GPS.
// Le dernier résultat reçu reste affiché pendant sa relecture ; il est « vérifié » 5 minutes après sa réception, tant que les
// données lues n'ont pas changé (clé, invalidation). Une lecture que plus personne n'attend est abandonnée : le serveur arrête son calcul.
const FRESH_MS = 5 * 60 * 1000;
const RETRY_MS = 5000;
const DAY_LIMIT = 3;
// Du moins récent au plus récent ; une journée garde ensemble ses entrées Jour et Soir.
const entries = new Map();
// Lectures de journée en cours (Consolidation) : aucune lecture Geotab affichée ne part pour ces dates en même temps.
const holds = new Set();
const listeners = new Set();
let version = 0;

// Clé des données lues par journée et période (`date:période`), fournie par la page Consolidation.
export const GeotabDayKeys = React.createContext(null);

/** Champs d'un relevé lus par le comparatif : réponse « Qui a conduit ? » et dîner. */
export const sheetGeotabFields = sheet => JSON.stringify([sheet.driver_check?.answer ?? null, sheet.driver_check?.employee_ids ?? null,
  sheet.driver_check?.geotab_name ?? null, sheet.lunch_start ?? null, sheet.lunch_end ?? null, Boolean(sheet.no_lunch)]);

function changed() {
  version += 1;
  listeners.forEach(listener => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Même version au rendu serveur (tests de mise en page) : ses effets ne s'exécutent pas, aucune lecture n'y part.
const snapshot = () => version;

const held = date => [...holds].some(range => range.from <= date && date <= range.to);

function isVerified(entry, readKey) {
  const result = entry?.result;
  return Boolean(result) && result.generation === entry.generation && Date.now() - result.at < FRESH_MS
    && (readKey === undefined || result.readKey === readKey);
}

// Réseau ou 5xx : une nouvelle tentative 5 s plus tard, sauf Geotab non configuré ; jamais pour une erreur 4xx.
const retryable = error => !error?.status || (error.status >= 500 && error.code !== "geotab_not_configured");

function touch(date, period) {
  const id = `${date}:${period}`;
  const entry = entries.get(id) || { id, date, period, generation: 0, result: null, read: null, readers: 0, waiters: [],
    wantKey: undefined, error: "", attempts: 0, retryTimer: null, expiry: null };
  for (const other of [...entries.values()].filter(item => item.date === date)) { entries.delete(other.id); entries.set(other.id, other); }
  entries.delete(id);
  entries.set(id, entry);
  return entry;
}

// Trois journées au plus : la moins récemment consultée part la première, jamais si un écran la lit ou si une lecture est en cours.
function prune() {
  const dates = [...new Set([...entries.values()].map(entry => entry.date))];
  let excess = dates.length - DAY_LIMIT;
  for (const date of dates) {
    if (excess <= 0) return;
    const day = [...entries.values()].filter(entry => entry.date === date);
    if (day.some(entry => entry.readers || entry.waiters.length || entry.read || entry.retryTimer)) continue;
    day.forEach(entry => { clearTimeout(entry.expiry); entries.delete(entry.id); });
    excess -= 1;
  }
}

function request(entry) {
  if (!entry.readers && !entry.waiters.length) return;
  if (entry.error || entry.retryTimer || isVerified(entry, entry.wantKey)) return;
  if (entry.read && entry.read.generation === entry.generation && entry.read.readKey === entry.wantKey) return;
  // La journée et ses champs de saisie d'abord ; la preuve GPS (waiters seuls) n'attend pas.
  if (!entry.waiters.length && held(entry.date)) return;
  entry.read?.controller.abort();
  const controller = new AbortController();
  const read = entry.read = { controller, generation: entry.generation, readKey: entry.wantKey };
  changed();
  fetchConsolidationGeotabComparison(entry.date, entry.period, { signal: controller.signal }).then(data => {
    // Remplacée, invalidée, abandonnée ou oubliée : ni gardée comme vérifiée, ni rendue.
    if (entry.read !== read) return;
    entry.read = null;
    entry.attempts = 0;
    entry.result = { data, at: Date.now(), generation: read.generation, readKey: read.readKey };
    entry.waiters.splice(0).forEach(waiter => waiter.resolve(data));
    clearTimeout(entry.expiry);
    entry.expiry = setTimeout(changed, FRESH_MS);
    changed();
    prune();
  }, error => {
    if (entry.read !== read) return;
    entry.read = null;
    entry.attempts += 1;
    if (entry.attempts === 1 && retryable(error)) {
      entry.retryTimer = setTimeout(() => { entry.retryTimer = null; changed(); if (entry.waiters.length) request(entry); }, RETRY_MS);
    } else {
      entry.error = error?.message || "Geotab est indisponible.";
      entry.waiters.splice(0).forEach(waiter => waiter.reject(error));
    }
    changed();
  });
}

// Le dernier lecteur parti l'abandonne un instant plus tard : un écran qui en remplace un autre la reprend sans la relancer.
function leave(entry) {
  entry.readers -= 1;
  setTimeout(() => {
    if (entry.readers || entry.waiters.length) return;
    entry.read?.controller.abort();
    entry.read = null;
    clearTimeout(entry.retryTimer);
    entry.retryTimer = null;
    changed();
    prune();
  });
}

// Les écrans demandent leur lecture à chaque changement (useGeotabDayState) ; seuls les appels de loadGeotabDay la demandent ici.
/** Lecture de la journée en cours, de from à to : les lectures Geotab affichées de ces dates partent à sa fin. */
export function holdGeotabDays(from, to) {
  const range = { from, to };
  holds.add(range);
  changed();
  return () => { if (holds.delete(range)) changed(); };
}

/** Données lues changées (Recharger, réponse « Qui a conduit ? », dîner) : l'aperçu reste affiché, non vérifié, et se relit. */
export function invalidateGeotabDay(date, period = "day") {
  const entry = entries.get(`${date}:${period}`);
  if (!entry) return;
  entry.generation += 1;
  entry.read?.controller.abort();
  entry.read = null;
  clearTimeout(entry.retryTimer);
  Object.assign(entry, { retryTimer: null, error: "", attempts: 0 });
  changed();
  if (entry.waiters.length) request(entry);
}

/** Changement de compte ou déconnexion : lectures annulées, mémoire vidée ; une réponse tardive est ignorée. */
export function forgetGeotabDays() {
  for (const entry of [...entries.values()]) {
    entry.read?.controller.abort();
    clearTimeout(entry.retryTimer);
    clearTimeout(entry.expiry);
    const waiters = entry.waiters.splice(0);
    Object.assign(entry, { generation: entry.generation + 1, result: null, read: null, error: "", attempts: 0, retryTimer: null, expiry: null });
    waiters.forEach(waiter => waiter.reject(new DOMException("Lecture Geotab annulée.", "AbortError")));
    if (!entry.readers) entries.delete(entry.id);
  }
  changed();
}

/** Résultat vérifié et frais, sinon la lecture partagée ; jamais un aperçu non vérifié. Un échec rejette la promesse. */
export function loadGeotabDay(date, period = "day") {
  const entry = touch(date, period);
  if (isVerified(entry)) return Promise.resolve(entry.result.data);
  return new Promise((resolve, reject) => {
    entry.waiters.push({ resolve, reject });
    Object.assign(entry, { error: "", attempts: 0 });
    request(entry);
    prune();
  });
}

const IDLE = { data: null, busy: false, refreshing: false, verified: false, error: "" };

export function useGeotabDayState(date, period, enabled) {
  const id = enabled && date ? `${date}:${period || "day"}` : "";
  const readKey = React.useContext(GeotabDayKeys)?.get(id);
  const current = React.useSyncExternalStore(subscribe, snapshot, snapshot);
  React.useEffect(() => {
    if (!id) return undefined;
    const entry = touch(date, period || "day");
    entry.readers += 1;
    prune();
    return () => leave(entry);
  }, [id]);
  React.useEffect(() => {
    const entry = id ? entries.get(id) : null;
    if (!entry) return;
    entry.wantKey = readKey;
    // Après les effets de la page : une lecture de la journée qui commence a déjà réservé sa date. Sans effet si rien n'a changé.
    queueMicrotask(() => request(entry));
  }, [id, readKey, current]);
  const retry = React.useCallback(() => {
    const entry = entries.get(id);
    if (entry) Object.assign(entry, { error: "", attempts: 0 });
    changed();
  }, [id]);
  if (!id) return { ...IDLE, retry };
  // Premier rendu compris : le résultat gardé de cette journée et période, jamais celui d'une autre.
  const entry = entries.get(id);
  const data = entry?.result?.data ?? null;
  const verified = isVerified(entry, readKey);
  const error = verified ? "" : entry?.error || "";
  const pending = !verified && !error;
  return { data, busy: pending && !data, refreshing: pending && Boolean(data), verified, error, retry };
}

export default function useGeotabDay(date, period, enabled) {
  return useGeotabDayState(date, period, enabled).data;
}
